import { ref } from 'vue'
import { useToast } from 'primevue/usetoast'
import { useConfirm } from 'primevue/useconfirm'
import { useI18n } from 'vue-i18n'
import * as userManagementService from '@/services/userManagementService'

export function useServiceManagement() {
  const toast = useToast()
  const confirm = useConfirm()
  const { t } = useI18n()
  
  const services = ref([])
  const serviceForm = ref({ name: '' })
  const isEditingService = ref(false)
  const editingServiceId = ref(null)
  const showServiceModal = ref(false)
  const showDeleteDialog = ref(false)
  const serviceToDelete = ref(null)
  
  // Add these new reactive variables for confirmation logic
  const selectedService = ref(null)
  const isDeleteService = ref(false)
  
  const loadServices = async () => {
    try {
      const response = await userManagementService.fetchServices()
      if (response && response.data) {
        // Fetch user count for each service
        const servicesWithUserCount = await Promise.all(
          response.data.map(async (service) => {
            try {
              const usersResponse = await userManagementService.getServiceUsers(service.id)
              console.log(usersResponse)
              return {
                ...service,
                user_count: usersResponse ? usersResponse.length : 0,
                users : usersResponse
              }
            } catch (error) {
              console.error(`Error fetching users for service ${service.id}:`, error)
              return {
                ...service,
                user_count: 0
              }
            }
          })
        )
        services.value = servicesWithUserCount
      } else {
        services.value = []
      }
    } catch (error) {
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: t('user_manage.failed_to_load_services'),
        life: 3000
      })
    }
  }

  const editService = (service) => {
    serviceForm.value = { ...service }
    editingServiceId.value = service.id
    isEditingService.value = true
    showServiceModal.value = true
  }

  const deleteService = async (service) => {
    selectedService.value = service
    isDeleteService.value = true
    confirm.require({
      message: `${t('user_manage.confirm_delete_service')} ${service?.name?.split('_')[0] || ''}`,
      header: t('common.confirm_deletion'),
      icon: 'pi pi-exclamation-triangle',
      rejectProps: {
        label: t('common.cancel')
      },
      acceptProps: {
        label: t('common.confirm')
      },
      accept: async () => {
        selectedService.value = null
        try {
          await userManagementService.deleteService(service.id)
          await loadServices()
          toast.add({ severity: 'success', summary: t('common.success'), detail: t('user_manage.service_deleted_successfully') })
          isDeleteService.value = false
        } catch (error) {
          toast.add({ severity: 'error', summary: t('common.error'), detail: t('user_manage.failed_to_delete_service') })
          isDeleteService.value = false
        }
      },
      onHide: () => {
        isDeleteService.value = false
      }
    })
  }

  const confirmDeleteService = async (service) => {
    try {
      await userManagementService.deleteService(service.id)
      toast.add({
        severity: 'success',
        summary: t('common.success'),
        detail: t('user_manage.service_deleted_successfully'),
        life: 3000
      })
      await loadServices()
    } catch (error) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to delete service',
        life: 3000
      })
    }
  }

  const saveService = async (serviceFormData) => {
    try {
      if (isEditingService.value) {
        await userManagementService.updateService(editingServiceId.value, {
          service_name: serviceFormData.name,
          users_ids: serviceFormData.selectedUsers || []
        })
        await userManagementService.patchService(editingServiceId.value, {
          responsible: serviceFormData.responsible ?? null,
          auto_report_enabled: serviceFormData.auto_report_enabled ?? false
        })
        toast.add({
          severity: 'success',
          summary: t('common.success'),
          detail: t('user_manage.service_updated_successfully'),
          life: 3000
        })
      } else {
        const createdService = await userManagementService.createService(serviceFormData)
        if (createdService?.id && serviceFormData.selectedUsers?.length) {
          await userManagementService.updateService(createdService.id, {
            service_name: serviceFormData.name,
            users_ids: serviceFormData.selectedUsers
          })
        }
        toast.add({
          severity: 'success',
          summary: t('common.success'),
          detail: t('user_manage.service_created_successfully'),
          life: 3000
        })
      }
      await loadServices()
      resetServiceForm()
    } catch (error) {
      toast.add({
        severity: 'error',
        summary: t('common.error'),
        detail: isEditingService.value ? t('user_manage.failed_to_update_service') : t('user_manage.failed_to_create_service'),
        life: 3000
      })
    }
  }

  const resetServiceForm = () => {
    serviceForm.value = { name: '', responsible: null, auto_report_enabled: false }
    isEditingService.value = false
    editingServiceId.value = null
    showServiceModal.value = false
  }

  const openNewServiceModal = () => {
    resetServiceForm()
    showServiceModal.value = true
  }

  return {
    services,
    serviceForm,
    isEditingService,
    showServiceModal,
    showDeleteDialog,
    serviceToDelete,
    selectedService,
    isDeleteService,
    loadServices,
    editService,
    deleteService,
    confirmDeleteService,
    saveService,
    resetServiceForm,
    openNewServiceModal
  }
}